import ModelBaseClass from "@quasidea/oas-client-react/lib/ModelBaseClass";
import ReconciliationLineItem from "../ReconciliationLineItem";
import ModelProxyClass from "./ModelProxyClass";
import Location from "../Location";
import CashAccount from "../CashAccount";
import Person from "../Person";


/**
 * @class ReconciliationLineItemBase
 * @extends ModelBaseClass
 * @property {string} identifier a unique identifier -- either c# or r# for Collections or Rt, and # is the db id
 * @property {number} cashTransactionId (int64) if sourced from Collections, this will be set.  if sourced from from RT, this will be null
 * @property {number} rtVendorTransactionId (int64) if sourced from RT, this will be set.  if sourced from Collections, this will be null
 * @property {number} reconciliationItemId (int64) only set if this has been matched
 * @property {'Collections'|'Rt'} source
 * @property {'Fill'|'RtAudit'|'BvDrop'|'StartDiscrepancy'|'EndDiscrepancy'|'ProcessDiscrepancyRtAudit'|'ProcessDiscrepancyBvDrop'} transactionType if source is Collections, then this is the TransactionType according to the Collections app
 * @property {string} rtVendorTransactionType if source is Rt, then this is the TransactionType according to the RT Vendor
 * @property {string} rtVendorIdentifier if source is Rt, then this is the Identifier according to the RT Vendor
 * @property {number} amount
 * @property {Location} location this will be NULL if type is StartDiscrepancy or EndDiscrepancy
 * @property {CashAccount} machineCashAccount this will be NULL if type is StartDiscrepancy or EndDiscrepancy
 * @property {CashAccount} vaultCashAccount this will be set if type is StartDiscrepancy or EndDiscrepancy
 * @property {Person} person if Collection-sourced, this will be the collector person or the vault tech person that is responsible for this line item
 * @property {boolean} hiddenFlag
 * @property {Date} dateCreated (date and time)
 */
class ReconciliationLineItemBase extends ModelBaseClass {
	/**
	 * a unique identifier -- either c# or r# for Collections or Rt, and # is the db id
	 * @type {string} identifier
	 */
	identifier;
	/**
	 * if sourced from Collections, this will be set.  if sourced from from RT, this will be null
	 * @type {number} cashTransactionId (int64)
	 */
	cashTransactionId;
	/**
	 * if sourced from RT, this will be set.  if sourced from Collections, this will be null
	 * @type {number} rtVendorTransactionId (int64)
	 */
	rtVendorTransactionId;
	/**
	 * only set if this has been matched
	 * @type {number} reconciliationItemId (int64)
	 */
	reconciliationItemId;
	/**
	 * @type {'Collections'|'Rt'} source
	 */
	source;
	/**
	 * if source is Collections, then this is the TransactionType according to the Collections app
	 * @type {'Fill'|'RtAudit'|'BvDrop'|'StartDiscrepancy'|'EndDiscrepancy'|'ProcessDiscrepancyRtAudit'|'ProcessDiscrepancyBvDrop'} transactionType
	 */
	transactionType;
	/**
	 * if source is Rt, then this is the TransactionType according to the RT Vendor
	 * @type {string} rtVendorTransactionType
	 */
	rtVendorTransactionType;
	/**
	 * if source is Rt, then this is the Identifier according to the RT Vendor
	 * @type {string} rtVendorIdentifier
	 */
	rtVendorIdentifier;
	/**
	 * @type {number} amount
	 */
	amount;
	/**
	 * this will be NULL if type is StartDiscrepancy or EndDiscrepancy
	 * @type {Location} location
	 */
	location;
	/**
	 * this will be NULL if type is StartDiscrepancy or EndDiscrepancy
	 * @type {CashAccount} machineCashAccount
	 */
	machineCashAccount;
	/**
	 * this will be set if type is StartDiscrepancy or EndDiscrepancy
	 * @type {CashAccount} vaultCashAccount
	 */
	vaultCashAccount;
	/**
	 * if Collection-sourced, this will be the collector person or the vault tech person that is responsible for this line item
	 * @type {Person} person
	 */
	person;
	/**
	 * @type {boolean} hiddenFlag
	 */
	hiddenFlag;
	/**
	 * @type {Date} dateCreated (date and time)
	 */
	dateCreated;

	/**
	 * Instantiates a new instance of ReconciliationLineItem based on the generic object being passed in (typically from a JSON object)
	 * @param {object} genericObject
	 * @return {ReconciliationLineItem}
	 */
	static create(genericObject) {
		const newReconciliationLineItem = new ReconciliationLineItem();
		newReconciliationLineItem.instantiate(_modelDefinition, genericObject, ModelProxyClass.createByClassName);
		return newReconciliationLineItem;
	}

	/**
	 * Instantiates a new array of ReconciliationLineItem based on the generic array being passed in (typically from a JSON array)
	 * @param {[object]} genericArray
	 * @return {[ReconciliationLineItem]}
	 */
	static createArray(genericArray) {
		if (genericArray === null) {
			return null;
		}

		const newReconciliationLineItemArray = [];
		genericArray.forEach(genericObject => {
			newReconciliationLineItemArray.push(ReconciliationLineItem.create(genericObject));
		});
		return newReconciliationLineItemArray;
	}
}

const _modelDefinition = [
	ModelBaseClass.createModelProperty('identifier', 'string'),
	ModelBaseClass.createModelProperty('cashTransactionId', 'integer'),
	ModelBaseClass.createModelProperty('rtVendorTransactionId', 'integer'),
	ModelBaseClass.createModelProperty('reconciliationItemId', 'integer'),
	ModelBaseClass.createModelProperty('source', 'string'),
	ModelBaseClass.createModelProperty('transactionType', 'string'),
	ModelBaseClass.createModelProperty('rtVendorTransactionType', 'string'),
	ModelBaseClass.createModelProperty('rtVendorIdentifier', 'string'),
	ModelBaseClass.createModelProperty('amount', 'float'),
	ModelBaseClass.createModelProperty('location', 'Location'),
	ModelBaseClass.createModelProperty('machineCashAccount', 'CashAccount'),
	ModelBaseClass.createModelProperty('vaultCashAccount', 'CashAccount'),
	ModelBaseClass.createModelProperty('person', 'Person'),
	ModelBaseClass.createModelProperty('hiddenFlag', 'boolean'),
	ModelBaseClass.createModelProperty('dateCreated', 'datetime'),
];

export default ReconciliationLineItemBase;
